import { setAuthHeader } from "@/api/service"
import { clearAuthData } from "@/helpers/clearAuthData"
import { parseAxiosError } from "@/helpers/parseAxiosError"
import { clearRefCode, getRefCode } from "@/helpers/refCode"
import { getTgUser, getTgUserId, getTgWebApp } from "@/helpers/tgWebApp"
import { AppDispatch } from "@/store"
import { TAGS, api } from "@/store/api"
import { setIsLabrUser } from "@/store/slices/app"
import { setIsAuthorized } from "@/store/slices/player"
import { IAuthTokensResponse, IWebAuthBody } from "./auth.types"

const basePath = "/auth"

const handleAuthResult = async (
  queryFulfilled: Promise<{ data: IAuthTokensResponse }>,
  dispatch: AppDispatch
) => {
  try {
    const { data } = await queryFulfilled
    setAuthHeader(data.accessToken, data.refreshToken)
    clearRefCode()
    dispatch(setIsAuthorized(true))
  } catch (e) {
    const { statusCode, message } = parseAxiosError(e)

    if (statusCode === 403 && message === "User is not a labrador member") {
      dispatch(setIsLabrUser(false))
    }

    dispatch(setIsAuthorized(false))
    clearAuthData()
  }
}

const withRefCode = (url: string): string => {
  const refCode = getRefCode()
  return refCode ? `${url}?refCode=${refCode}` : url
}

export const authApi = api.enhanceEndpoints({
  addTagTypes: [TAGS.AUTH]
})

export const authEndpoints = authApi.injectEndpoints({
  endpoints: (builder) => ({
    auth: builder.mutation<IAuthTokensResponse, void>({
      query: () => ({
        url: withRefCode(`${basePath}/telegram`),
        method: "POST",
        data: {
          initData: getTgWebApp()?.initData,
          id: getTgUserId()?.toString(),
          photo_url: getTgUser()?.photo_url?.toString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      }),
      extraOptions: { maxRetries: 1 },
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        await handleAuthResult(queryFulfilled, dispatch)
      }
    }),

    webAuth: builder.mutation<IAuthTokensResponse, IWebAuthBody>({
      query: (data) => ({
        url: withRefCode(`${basePath}/web`),
        method: "POST",
        data
      }),
      extraOptions: { maxRetries: 1 },
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        await handleAuthResult(queryFulfilled, dispatch)
      }
    }),

    sendEmailCodeLogin: builder.mutation<void, { email: string }>({
      query: (data) => ({
        url: `${basePath}/email/send-code/login`,
        method: "POST",
        data
      })
    }),

    sendEmailCodeCreate: builder.mutation<void, { email: string }>({
      query: (data) => ({
        url: `${basePath}/email/send-code/create`,
        method: "POST",
        data
      })
    }),

    verifyEmailCode: builder.mutation<
      IAuthTokensResponse,
      { email: string; code: string }
    >({
      query: (data) => ({
        url: withRefCode(`${basePath}/email/verify-code`),
        method: "POST",
        data
      }),
      extraOptions: { maxRetries: 1 },
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        await handleAuthResult(queryFulfilled, dispatch)
      }
    }),

    refresh: builder.mutation<
      { _id: string; accessToken: string; refreshToken: string },
      void
    >({
      query: () => ({
        url: `${basePath}/refresh`,
        method: "POST"
      }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled
          setAuthHeader(data.accessToken, data.refreshToken)
          dispatch(setIsAuthorized(true))
        } catch {
          dispatch(setIsAuthorized(false))
          clearAuthData()
        }
      }
    })
  })
})

export const {
  useAuthMutation,
  useWebAuthMutation,
  useSendEmailCodeLoginMutation,
  useSendEmailCodeCreateMutation,
  useVerifyEmailCodeMutation,
  useRefreshMutation,
  endpoints: {
    auth,
    webAuth,
    sendEmailCodeLogin,
    sendEmailCodeCreate,
    verifyEmailCode,
    refresh
  }
} = authEndpoints
